import {singleton} from "../../infrastructure/decorators/singleton";
import {AnalyticsService} from "../../third-party-services/analytics/analytics-service";
import {BaseService} from "../../infrastructure/domain-services/base-service";
import {VideoEmbeddedDisplayEvent} from "../models/events/video-embeded-display.analytics-event";
import {BaseAnalyticsEvent} from "../../infrastructure/models/analytics/base-analytics-event";
import {VIDEO_EMBEDDED} from "../components/video-highlight/video-embedded.consts";
import {GAME_STATUSES} from "../app/consts";
import {VideoEmbeddedPlayClickEvent} from "../models/events/video-embeded-play-click.analytics-event";
import {VideoEmbeddedEndedEvent} from "../models/events/video-embeded-ended.analytics-event";

@singleton
export class YoutubeVideoManager extends BaseService {
  analyticsService: AnalyticsService;
  player: String;
  gameId: Number;
  videoId: String;
  gameStatus: Number;
  playerState: Number;
  callbacks: Array<Object> = [];
  autoplayEnabled: boolean = false;

  static injectables = [...BaseService.injectables, AnalyticsService];

  constructor(analyticsService: AnalyticsService) {
    super();

    this.analyticsService = analyticsService;

    this.initVideoPlayer = this.initVideoPlayer.bind(this);
    this.onPlayerReady = this.onPlayerReady.bind(this);
    this.onPlayerStateChange = this.onPlayerStateChange.bind(this);
  }

  loadYoutubeIFrameApiScript(height: Number, width: Number, videoId: String, gameStatus: Number, gameId: Number) {
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";

    const firstScriptTag = document.getElementsByTagName("script")[0];
    firstScriptTag.parentNode.insertBefore(tag, firstScriptTag);

    tag.onload = () => this.createNewPlayer(height, width, videoId, gameStatus, gameId);
  }

  initVideoPlayer(height: Number, width: Number, videoId: String, gameStatus: Number, gameId: Number) {
    if (!this.player) {
      this.loadYoutubeIFrameApiScript(height, width, videoId, gameStatus, gameId);
    } else {
      this.createNewPlayer(height, width, videoId, gameStatus, gameId);
    }
  }

  stopVideo() {
    this.player.stopVideo();
  }

  setAutoplay(enabled: boolean) {
    this.autoplayEnabled = enabled;
  }

  createNewPlayer(height: Number, width: Number, videoId: String, gameStatus: Number, gameId: Number) {
    window.YT.ready(() => {
      this.player = new window.YT.Player(videoId, {
        height: height,
        width: width,
        videoId: videoId,
        events: {
          'onReady': (event) => {
            this.onPlayerReady(event, gameId, videoId, gameStatus)
            if (this.autoplayEnabled) {
              this.player?.playVideo(); // Play automatically if enabled
            } else {
              this.player?.cueVideoById?.(videoId); // Preload the video if autoplay is off
            }
          },
          'onStateChange': (...event) => this.onPlayerStateChange(...event, gameId, videoId, gameStatus)
        },
      });
    });
  }

  onPlayerReady(event, gameId, videoId, status) {
    this.setPlayerState(event, videoId)

    const params = {
      videoId,
      gameId,
      status: this.getGameStatus(status),
      totalDuration: this.player?.getDuration?.(),
      isPreview: VIDEO_EMBEDDED.ANALYTICS.VIDEO_EVENT_IS_PREVIEW_STATUS.ON_CLICK,
    }
    this.sendEvent(params, VideoEmbeddedDisplayEvent);
  }

  onPlayerStateChange(event, gameId, videoId, status) {
    this.setPlayerState(event, videoId)
    switch (event.data) {
      case VIDEO_EMBEDDED.ANALYTICS.YT.PlayerState.ENDED: {
        const params = {
          videoId,
          gameId,
          status: this.getGameStatus(status)
        }
        this.sendEvent(params, VideoEmbeddedEndedEvent);
        break;
      }
      case VIDEO_EMBEDDED.ANALYTICS.YT.PlayerState.PLAYING:
      case VIDEO_EMBEDDED.ANALYTICS.YT.PlayerState.PAUSED: {
        const params = {
          videoId,
          gameId,
          status: this.getGameStatus(status),
          totalDuration: this.player?.getDuration?.(),
          timeSeen: this.calcWatchedDurationPercentage(),
          isPreview: VIDEO_EMBEDDED.ANALYTICS.VIDEO_EVENT_IS_PREVIEW_STATUS.ON_CLICK,
        }
        this.sendEvent(params, VideoEmbeddedPlayClickEvent);
        break;
      }
      default:
        return;
    }
  }

  switchVideo(videoId: String) {
    if (this.player && typeof this.player.loadVideoById === "function") {
      this.player.loadVideoById(videoId);
    } else {
      window.YT.ready(() => {
        setTimeout(() => {
          if (this.player && typeof this.player.loadVideoById === "function") {
            this.player.loadVideoById(videoId);
          }
        }, 500);
      });
    }
  }

  calcWatchedDurationPercentage(): Number {
    const timePlayed = this.player?.getCurrentTime?.();
    const totalDuration = this.player?.getDuration?.();
    const timePeriods = [0, 0.25, 0.5, 0.75, 1];

    const pctPlayed = timePlayed / totalDuration;

    const closest = timePeriods.reduce((a, b) => {
      return Math.abs(b - pctPlayed) < Math.abs(a - pctPlayed) ? b : a;
    });

    return closest;
  }

  sendEvent(params, eventType) {
    const VideoEmbeddedEvent: BaseAnalyticsEvent = new eventType(params);

    this.playerState >= 0 && this.analyticsService.createEvent(VideoEmbeddedEvent);
  }

  getGameStatus(status): Number {
    switch (status) {
      case GAME_STATUSES.ended:
        return VIDEO_EMBEDDED.ANALYTICS.VIDEO_EVENT_GAME_STATUS.AFTER;
      case GAME_STATUSES.scheduled:
        return VIDEO_EMBEDDED.ANALYTICS.VIDEO_EVENT_GAME_STATUS.BEFORE;
      case GAME_STATUSES.started:
        return VIDEO_EMBEDDED.ANALYTICS.VIDEO_EVENT_GAME_STATUS.LIVE;
      default:
        return null;
    }
  }

  unMountVideoEvent(gameId, videoId, status) {
    const params = {
      videoId,
      gameId,
      status: this.getGameStatus(status),
      totalDuration: this.player?.getDuration?.(),
      timeSeen: this.calcWatchedDurationPercentage(),
      isPreview: VIDEO_EMBEDDED.ANALYTICS.VIDEO_EVENT_IS_PREVIEW_STATUS.ON_CLICK,
    }
    this.sendEvent(params, VideoEmbeddedPlayClickEvent);
  }

  registerToStateChange(id, callback) {
    this.callbacks.push({id, callback});
  }

  unregisterCallback(id) {
    const callbackIndex = this.callbacks.findIndex((callback: Object) => callback.id === id);

    if (callbackIndex > -1) {
      this.callbacks.splice(callbackIndex, 1);
    }
  }

  setPlayerState(event, videoId) {
    this.playerState = event.target.getPlayerState()
    this.callbacks.find(call => call.id === videoId)?.callback?.(this.playerState)
  }
}


/* <iframe
src="https://www.youtube.com/embed/QH2-TGUlwu4?rel=0"
 width="560"
  height="315"
   title="Nyan Cat [original]"
   frameBorder="0"
    allowFullScreen
   ></iframe>
 */

/*<iframe
src="https://www.youtube.com/embed/QH2-TGUlwu4"
width="560"
 height="315"
 title="Nyan Cat [original]"
 frameborder="0"
  allowfullscreen></iframe>*/
